import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledPopover,
  PopoverBody,
} from "reactstrap";
import { CircleAlert, MoreVertical } from "lucide-react";
import { useDraggable, useDroppable } from "@dnd-kit/core";
import {
  updateStoreProjectItem,
  deleteStoreProjectItem,
  cloneStoreProjectItem,
} from "../../service";
import {
  useStableNewVariantAxes,
  pickAxisProductId,
  pickVariantSizeProductIds,
  resolveVariantSizeAxes,
  toNullableNumber,
  StoreNewVariantAxisSelectOptions,
  StoreProductVariantDimensionPickers,
} from "../../variantAxes";
import {
  getProjectItemProductId,
  countProductUnitsInFolderSubtree,
  formatSizeForDisplay,
  isTouchUi,
  getProjectItemDisplayName,
  getProductThumbUrl,
  getProductLinePricing,
  getDeliveryTextForSelectedVariant,
  toPriceNum,
  formatInr,
} from "./cartUtils";

const CartCloneFlashContext = createContext(null);

const CLONE_FLASH_DURATION_MS = 2000;

/**
 * Wraps the project cart tree so clone responses can briefly highlight the new row.
 */
export function CartCloneFlashProvider({ children }) {
  const [cloneFlashItemId, setCloneFlashItemId] = useState(null);
  const clearTimerRef = useRef(null);

  useEffect(
    () => () => {
      if (clearTimerRef.current != null) {
        window.clearTimeout(clearTimerRef.current);
      }
    },
    []
  );

  const flashCloneItem = useCallback((rawId) => {
    const idNum = Number(rawId);
    if (Number.isNaN(idNum)) return;
    if (clearTimerRef.current != null) {
      window.clearTimeout(clearTimerRef.current);
    }
    setCloneFlashItemId(idNum);
    clearTimerRef.current = window.setTimeout(() => {
      setCloneFlashItemId(null);
      clearTimerRef.current = null;
    }, CLONE_FLASH_DURATION_MS);
  }, []);

  const value = useMemo(
    () => ({ cloneFlashItemId, flashCloneItem }),
    [cloneFlashItemId, flashCloneItem]
  );

  return (
    <CartCloneFlashContext.Provider value={value}>
      {children}
    </CartCloneFlashContext.Provider>
  );
}

export function useCartCloneFlash() {
  const ctx = useContext(CartCloneFlashContext);
  return ctx ?? { cloneFlashItemId: null, flashCloneItem: undefined };
}

export function ProjectItemTree({
  items,
  projectId,
  onRefresh,
  selectedItemId,
  onSelectItem,
  draggingItemId,
  isDraggingItem,
  activeDropTargetId = null,
  mobileActionsItemId,
  onToggleMobileActions,
  canEditProjectCart,
  canMutateProjectCart,
  canRemoveProductInCartMode = false,
  reorderDropTargetsEnabled = false,
  canShowPrice,
  usePersistedItemPrices = false,
  canCloneItems = false,
  isSampleQuote = false,
  onNonCustomerProductDecrement,
  openFolderIds,
  onToggleFolder,
  onOpenProductDetail,
  onAuthRequired,
  onDuplicateProduct,
  /** 0 = cart root; nested folders get depth + 1 for visual hierarchy */
  depth = 0,
}) {
  if (!items || items.length === 0) {
    return null;
  }

  return (
    <div
      className={`store-project-tree ${
        depth > 0 ? "store-project-tree--nested" : "store-project-tree--root"
      }`}
      data-tree-depth={depth}
    >
      {items.map((item) => (
        <ProjectItemNode
          key={item.id}
          item={item}
          projectId={projectId}
          onRefresh={onRefresh}
          selectedItemId={selectedItemId}
          onSelectItem={onSelectItem}
          draggingItemId={draggingItemId}
          isDraggingItem={isDraggingItem}
          activeDropTargetId={activeDropTargetId}
          mobileActionsItemId={mobileActionsItemId}
          onToggleMobileActions={onToggleMobileActions}
          canEditProjectCart={canEditProjectCart}
          canMutateProjectCart={canMutateProjectCart}
          canRemoveProductInCartMode={canRemoveProductInCartMode}
          reorderDropTargetsEnabled={reorderDropTargetsEnabled}
          canShowPrice={canShowPrice}
          usePersistedItemPrices={usePersistedItemPrices}
          canCloneItems={canCloneItems}
          isSampleQuote={isSampleQuote}
          onNonCustomerProductDecrement={onNonCustomerProductDecrement}
          openFolderIds={openFolderIds}
          onToggleFolder={onToggleFolder}
          onOpenProductDetail={onOpenProductDetail}
          onAuthRequired={onAuthRequired}
          onDuplicateProduct={onDuplicateProduct}
          depth={depth}
          isExpanded={
            item.item_type === "folder" ? openFolderIds.has(item.id) : false
          }
          onToggleExpand={
            item.item_type === "folder"
              ? () => onToggleFolder(item.id)
              : undefined
          }
        />
      ))}
    </div>
  );
}

/**
 * Single item node component.
 */
function ProjectItemNode({
  item,
  projectId,
  onRefresh,
  selectedItemId,
  onSelectItem,
  draggingItemId,
  isDraggingItem,
  activeDropTargetId = null,
  mobileActionsItemId,
  onToggleMobileActions,
  canEditProjectCart = false,
  canMutateProjectCart = false,
  canRemoveProductInCartMode = false,
  reorderDropTargetsEnabled = false,
  canShowPrice = false,
  usePersistedItemPrices = false,
  canCloneItems = false,
  isSampleQuote = false,
  onNonCustomerProductDecrement,
  openFolderIds,
  onToggleFolder,
  depth = 0,
  isExpanded = false,
  onToggleExpand,
  onOpenProductDetail,
  onAuthRequired,
  onDuplicateProduct,
}) {
  const { cloneFlashItemId, flashCloneItem } = useCartCloneFlash();
  const showCloneFlash =
    cloneFlashItemId != null && Number(item.id) === Number(cloneFlashItemId);

  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(() =>
    getProjectItemDisplayName(item)
  );
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSavingName, setIsSavingName] = useState(false);
  const [isSavingVariant, setIsSavingVariant] = useState(false);
  const [isVariantEditorOpen, setIsVariantEditorOpen] = useState(false);

  const isFolder = item.item_type === "folder";
  const isEditingRow = isEditing;
  const hasChildren = item.children && item.children.length > 0;
  const inFolder = depth > 0;
  const mobileActionsOpen = mobileActionsItemId === item.id;
  /** Cart project (non-cart) draft lines: same line edits as non-cart project (qty, variant). */
  const canEditLineLikeProject =
    canMutateProjectCart || canRemoveProductInCartMode;

  const pointerOrTouchRowMobile = !isTouchUi() || mobileActionsOpen;
  /** Collapsed folders: always show a handle next to the chevron when the cart is reorderable. */
  const showFolderCollapsedDrag =
    isFolder &&
    !isExpanded &&
    reorderDropTargetsEnabled &&
    canMutateProjectCart;
  /** Expanded folder: same touch rule as product rows (tap row to reveal move handle). */
  const showFolderExpandedMoveSlot =
    isFolder &&
    isExpanded &&
    reorderDropTargetsEnabled &&
    canMutateProjectCart &&
    pointerOrTouchRowMobile;
  const showProductDragChrome =
    !isFolder &&
    reorderDropTargetsEnabled &&
    canMutateProjectCart &&
    pointerOrTouchRowMobile;

  const showDragChrome =
    showProductDragChrome ||
    showFolderCollapsedDrag ||
    showFolderExpandedMoveSlot;
  const canDragThisRow = showDragChrome;

  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    isDragging,
  } = useDraggable({
    id: `mv-${item.id}`,
    data: { itemId: item.id },
    disabled: !canDragThisRow || isEditingRow,
  });
  /** DragOverlay shows the moving card — collapse in-flow source so no “empty” strip remains. */
  const dragSourceHiddenInList =
    reorderDropTargetsEnabled && canDragThisRow && isDragging;

  const { setNodeRef: setFolderDropRef, isOver: isFolderDropOver } =
    useDroppable({
      id: `fld-${item.id}`,
      disabled:
        !isFolder || draggingItemId === item.id || !reorderDropTargetsEnabled,
      data: { folderId: item.id },
    });
  const { setNodeRef: setFolderHeaderDropRef, isOver: isFolderHeaderDropOver } =
    useDroppable({
      id: `fldh-${item.id}`,
      disabled:
        !isFolder || draggingItemId === item.id || !reorderDropTargetsEnabled,
      data: { folderId: item.id },
    });
  const { setNodeRef: setOrderDropRef } = useDroppable({
    id: `ord-${item.id}`,
    disabled:
      draggingItemId === item.id ||
      !reorderDropTargetsEnabled ||
      /* Folders drop via `fld-*` / `fldh-*` only — `ord-*` on headers caused layout gaps. */
      isFolder,
    data: { itemId: item.id },
  });
  const setFolderHeaderRowRef = useCallback(
    (node) => {
      setFolderHeaderDropRef(node);
      setOrderDropRef(node);
      setDraggableNodeRef(node);
    },
    [setFolderHeaderDropRef, setOrderDropRef, setDraggableNodeRef]
  );
  const setProductCartRowRef = useCallback(
    (node) => {
      setOrderDropRef(node);
      setDraggableNodeRef(node);
    },
    [setOrderDropRef, setDraggableNodeRef]
  );

  const showReorderInsertGap =
    !isFolder &&
    reorderDropTargetsEnabled &&
    draggingItemId != null &&
    Number(draggingItemId) !== Number(item.id) &&
    activeDropTargetId === `ord-${item.id}`;

  useEffect(() => {
    if (!isEditing) {
      setEditName(getProjectItemDisplayName(item));
    }
  }, [item.id, item.name, item.product_id, item.item_type, isEditing]);

  useEffect(() => {
    if (!isEditing) return;
    setIsVariantEditorOpen(false);
  }, [isEditing]);

  const isSelected = selectedItemId === item.id;
  useEffect(() => {
    if (isSelected) return;
    setIsVariantEditorOpen(false);
  }, [isSelected]);
  const isDraggingThis = draggingItemId === item.id;
  const axes = useStableNewVariantAxes(item);
  const listProductId = toNullableNumber(item?.product_id);
  const {
    heightOpts,
    widthOpts,
    depthOpts,
  } = resolveVariantSizeAxes(axes);
  const { heightPid, widthPid, depthPid } =
    pickVariantSizeProductIds(axes, listProductId);
  const rangeOpts = axes?.range ?? [];
  const rangePid = pickAxisProductId(rangeOpts, listProductId);
  const displaySizeLine = formatSizeForDisplay(item?.size);
  const displayRangeLine = String(item?.range ?? "").trim();
  const displaySizeRangeLine =
    displaySizeLine && displayRangeLine
      ? `${displaySizeLine} - ${displayRangeLine}`
      : displaySizeLine || displayRangeLine;
  const showAxisVariantUi =
    rangeOpts.length > 0 ||
    heightOpts.length > 0 ||
    widthOpts.length > 0 ||
    depthOpts.length > 0;
  const showSizeRangeChangeLink =
    canEditLineLikeProject && showAxisVariantUi;
  const showSizeRangeEditorPanel =
    isSelected &&
    isVariantEditorOpen &&
    canEditLineLikeProject &&
    showAxisVariantUi;

  const productDeliveryLabel = !isFolder
    ? getDeliveryTextForSelectedVariant({
        currentProduct: item,
        heightOpts,
        widthOpts,
        depthOpts,
        heightPid,
        widthPid,
        depthPid,
      })
    : null;

  const discardNameEdit = useCallback(() => {
    setIsEditing(false);
    setEditName(getProjectItemDisplayName(item));
  }, [item]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!canMutateProjectCart) return;
    if (!editName.trim()) return;
    if (isSavingName) return;

    setIsSavingName(true);
    try {
      await updateStoreProjectItem(projectId, item.id, {
        name: editName.trim(),
      });
      setIsEditing(false);
      onRefresh();
    } catch (error) {
      console.error("Failed to update item:", error);
      alert(error?.response?.data?.error || "Failed to update item");
    } finally {
      setIsSavingName(false);
    }
  };

  const handleCloneItem = async (e) => {
    e.stopPropagation();
    if (!canCloneItems) return;
    if (!isFolder) {
      onDuplicateProduct?.(item);
      return;
    }
    try {
      const res = await cloneStoreProjectItem(projectId, item.id);
      const newId = res?.data?.id;
      await onRefresh?.();
      if (newId != null) {
        requestAnimationFrame(() => {
          flashCloneItem?.(newId);
        });
      }
    } catch (error) {
      alert(
        error?.response?.data?.error?.message ||
          error?.response?.data?.error ||
          "Failed to duplicate item"
      );
    }
  };

  const handleDelete = async () => {
    if (!canMutateProjectCart) return;
    setIsDeleting(true);
    try {
      await deleteStoreProjectItem(projectId, item.id);
      setShowDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Failed to delete item:", error);
      alert(error?.response?.data?.error || "Failed to delete item");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleVariantProductId = useCallback(
    async (nextRaw) => {
      if (!canEditLineLikeProject) return;
      const nid = toNullableNumber(nextRaw);
      if (nid == null) return;
      const currentPid = getProjectItemProductId(item);
      if (currentPid != null && Number(currentPid) === Number(nid)) return;
      setIsSavingVariant(true);
      try {
        const targetIds = [item.id];
        for (const rowId of targetIds) {
          await updateStoreProjectItem(projectId, rowId, { product_id: nid });
        }
        await onRefresh?.();
      } catch (err) {
        alert(
          err?.response?.data?.error || err?.message || "Failed to save variant"
        );
      } finally {
        setIsSavingVariant(false);
      }
    },
    [canEditLineLikeProject, item, projectId, onRefresh]
  );

  const thumbUrl = getProductThumbUrl(item);
  const { salePriceText, listPriceText, unitText } = getProductLinePricing(
    item,
    {
      usePersistedPricesOnly: usePersistedItemPrices,
    }
  );
  const sampleListNum = toPriceNum(item.price);
  const sampleDiscNum = toPriceNum(item.discount_price);
  const sampleListPriceText =
    sampleListNum != null &&
    sampleDiscNum != null &&
    sampleListNum !== sampleDiscNum
      ? formatInr(sampleListNum)
      : listPriceText;
  const showSampleListPrice =
    isSampleQuote && sampleListPriceText != null && salePriceText != null;
  const showPricing = Boolean(salePriceText || unitText);
  const stalePricePopoverEligible =
    item.price_stale && canShowPrice && item.current_discount_price != null;
  const quotedSaleForStaleRow =
    salePriceText ||
    (toPriceNum(item.discount_price) != null
      ? formatInr(Math.round(toPriceNum(item.discount_price)))
      : null);
  /** Non-touch: show inline variant/actions. Touch: row must be tapped (--mobile-actions). */
  const lineActionsActive = !isTouchUi() || mobileActionsOpen;
  const showProductRenamePencil =
    !isFolder &&
    showSizeRangeChangeLink &&
    lineActionsActive &&
    !isVariantEditorOpen &&
    !isEditing &&
    canMutateProjectCart;
  const showProductQuickActionsToolbar =
    !isFolder &&
    lineActionsActive &&
    (canMutateProjectCart || canRemoveProductInCartMode);

  const folderDropHighlight =
    isFolder &&
    isDraggingItem &&
    (isFolderDropOver || isFolderHeaderDropOver) &&
    draggingItemId !== item.id
      ? {
          outline: "2px dashed #0d6efd",
          outlineOffset: "2px",
          boxShadow: "0 0 0 9999px #fdea9f inset",
        }
      : {};

  const folderProductCount = isFolder
    ? countProductUnitsInFolderSubtree(item)
    : 0;
  const productItemLabel =
    !isFolder &&
    item.label != null &&
    String(item.label).trim() !== ""
      ? String(item.label).trim()
      : null;
  const folderSqftNum = isFolder ? toPriceNum(item?.sqft_price) : null;
  const folderSqftText =
    canShowPrice && folderSqftNum != null
      ? `${formatInr(folderSqftNum)} / sqft`
      : null;

  const handleFolderHeaderClick = (e) => {
    onSelectItem(item.id);
    if (isEditing) return;
    if (!isTouchUi() || !onToggleMobileActions) return;
    if (
      e.target.closest(
        ".store-project-section__move-slot, .store-project-section__kebab-slot, .store-project-folder-accordion__toggle, .store-products-accordion__chevron, .store-project-folder-accordion__inline-move"
      )
    ) {
      return;
    }
    onToggleMobileActions(item.id);
  };

  const handleItemRowClick = (e) => {
    onSelectItem(item.id);
    if (
      e.target.closest(
        ".store-project-cart-line__drag, .store-project-cart-line__kebab, .store-project-kebab, [data-store-cart-quick-actions], button, a, input, select, textarea"
      )
    ) {
      return;
    }
    if (e.target.closest(".store-products-row__controls")) {
      return;
    }
    // Touch: taps outside the title toggle `--mobile-actions` (product modal only from title).
    if (!isTouchUi() || !onToggleMobileActions) return;
    onToggleMobileActions(item.id);
  };

  const handleOpenProductDetailFromLine = (e) => {
    e.stopPropagation();
    if (!onOpenProductDetail) return;
    const pid = getProjectItemProductId(item);
    if (pid == null) return;
    onOpenProductDetail(item);
  };

  return (
    <div className="store-project-tree-node m-0">
      {isFolder ? (
        <div
          className="store-products-accordion store-project-folder-accordion"
          ref={setFolderDropRef}
        >
          <div
            tabIndex={0}
            ref={setFolderHeaderRowRef}
            className={`store-project-folder-header-row ${
              mobileActionsOpen ? "bg-primary-subtle" : "bg-white"
            } ${isSelected ? "is-selected" : ""} ${
              showCloneFlash ? "store-project-cart-line--clone-flash" : ""
            } ${
              dragSourceHiddenInList
                ? "store-project-folder-header-row--drag-source-hidden"
                : ""
            }`}
            style={{
              cursor: "pointer",
              ...folderDropHighlight,
            }}
            onClick={handleFolderHeaderClick}
          >
            {isEditingRow ? (
              <form
                onSubmit={handleUpdate}
                className="d-flex align-items-center flex-grow-1 flex-wrap gap-2 w-100 px-1 py-1"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="text"
                  className="form-control form-control-sm text-dark unmask"
                  style={{ minWidth: 160, maxWidth: "100%" }}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === "Escape") {
                      discardNameEdit();
                    }
                  }}
                />
                <button
                  type="submit"
                  className="btn btn-sm btn-light fw-semibold"
                  disabled={isSavingName || !editName.trim()}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  disabled={isSavingName}
                  onClick={discardNameEdit}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {showFolderExpandedMoveSlot ? (
                  <div className="store-project-section__move-slot">
                    <div
                      role="button"
                      tabIndex={0}
                      className={`store-project-section__move ${
                        isDraggingThis ? "is-active" : ""
                      }`}
                      title="Drag to a folder or root drop zone"
                      aria-label="Move section"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          e.stopPropagation();
                      }}
                      {...listeners}
                      {...attributes}
                    >
                      <i className="fa-solid fa-bars" aria-hidden />
                    </div>
                  </div>
                ) : null}
                <button
                  type="button"
                  className={`store-products-accordion__header store-project-folder-accordion__toggle w-100 d-flex align-items-center gap-2 border-0 text-start fw-bold ${
                    mobileActionsOpen ? "bg-transparent" : "bg-white"
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleExpand?.();
                  }}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
                >
                  <span
                    className="store-products-accordion__chevron"
                    aria-hidden
                  >
                    {isExpanded ? "▼" : "▶"}
                  </span>
                  {showFolderCollapsedDrag ? (
                    <div
                      role="button"
                      tabIndex={0}
                      className={`store-project-folder-accordion__inline-move store-project-section__move ${
                        isDraggingThis ? "is-active" : ""
                      }`}
                      title="Drag to a folder or root drop zone"
                      aria-label="Move section"
                      onClick={(e) => e.stopPropagation()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ")
                          e.stopPropagation();
                      }}
                      {...listeners}
                      {...attributes}
                    >
                      <i className="fa-solid fa-bars" aria-hidden />
                    </div>
                  ) : null}
                  <span className="store-products-accordion__header-title text-uppercase flex-grow-1">
                    {getProjectItemDisplayName(item)} ({folderProductCount})
                    {folderSqftText ? (
                      <span className="store-products-accordion__header-price">
                        {folderSqftText}
                      </span>
                    ) : null}
                    {item.notes ? (
                      <span className="d-block small text-muted fw-normal mt-1">
                        {item.notes}
                      </span>
                    ) : null}
                  </span>
                </button>
                {canMutateProjectCart ? (
                  <div className="store-project-section__kebab-slot">
                    <UncontrolledDropdown
                      className="d-flex align-items-start"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <DropdownToggle
                        tag="button"
                        type="button"
                        className="store-project-kebab"
                        caret={false}
                        aria-label="Section actions"
                      >
                        <MoreVertical size={18} />
                      </DropdownToggle>
                      <DropdownMenu end strategy="fixed" container="body">
                        <DropdownItem
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canMutateProjectCart) return;
                            setIsEditing(true);
                          }}
                        >
                          <i className="fa-solid fa-pencil me-2" aria-hidden />
                          Rename
                        </DropdownItem>
                        {canCloneItems ? (
                          <DropdownItem
                            onClick={handleCloneItem}
                          >
                            <i className="fa-solid fa-clone me-2" aria-hidden />
                            Duplicate
                          </DropdownItem>
                        ) : null}
                        <DropdownItem
                          className="text-danger"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!canMutateProjectCart) return;
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <i className="fa-solid fa-trash me-2" aria-hidden />
                          Delete
                        </DropdownItem>
                      </DropdownMenu>
                    </UncontrolledDropdown>
                  </div>
                ) : null}
              </>
            )}
          </div>

          {isExpanded && hasChildren && (
            <div className="store-products-accordion__body store-project-folder-accordion__body">
              <ProjectItemTree
                items={item.children}
                projectId={projectId}
                onRefresh={onRefresh}
                selectedItemId={selectedItemId}
                onSelectItem={onSelectItem}
                draggingItemId={draggingItemId}
                isDraggingItem={isDraggingItem}
                activeDropTargetId={activeDropTargetId}
                mobileActionsItemId={mobileActionsItemId}
                onToggleMobileActions={onToggleMobileActions}
                canEditProjectCart={canEditProjectCart}
                canMutateProjectCart={canMutateProjectCart}
                canRemoveProductInCartMode={canRemoveProductInCartMode}
                reorderDropTargetsEnabled={reorderDropTargetsEnabled}
                canShowPrice={canShowPrice}
                usePersistedItemPrices={usePersistedItemPrices}
                canCloneItems={canCloneItems}
                isSampleQuote={isSampleQuote}
                onNonCustomerProductDecrement={onNonCustomerProductDecrement}
                openFolderIds={openFolderIds}
                onToggleFolder={onToggleFolder}
                onOpenProductDetail={onOpenProductDetail}
                onAuthRequired={onAuthRequired}
                onDuplicateProduct={onDuplicateProduct}
                depth={depth + 1}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          tabIndex={0}
          ref={setProductCartRowRef}
          className={`store-products-row store-project-cart-line ${
            isSelected ? "is-selected" : ""
          } ${inFolder ? "store-project-cart-line--in-folder" : ""} ${
            mobileActionsOpen
              ? "store-project-cart-line--mobile-actions bg-primary-subtle"
              : ""
          } ${showCloneFlash ? "store-project-cart-line--clone-flash" : ""} ${
            showReorderInsertGap ? "store-project-cart-line--reorder-gap" : ""
          } ${
            dragSourceHiddenInList
              ? "store-project-cart-line--drag-source-hidden"
              : ""
          }`}
          style={{
            cursor: "pointer",
          }}
          onClick={handleItemRowClick}
          onKeyDown={(e) => {
            if (isEditing) return;
            if (e.key !== "Enter" && e.key !== " ") return;
            e.preventDefault();
            handleItemRowClick(e);
          }}
        >
          <>
            <div className="d-flex flex-row align-items-stretch flex-grow-1 w-100 min-w-0 gap-2">
              {showDragChrome ? (
                <div className="store-project-cart-line__drag d-flex align-items-start justify-content-center flex-shrink-0">
                  <div
                    role="button"
                    tabIndex={0}
                    className={`store-project-cart-line__move ${
                      isDraggingThis ? "is-active" : ""
                    }`}
                    title="Drag to a folder or root drop zone"
                    aria-label="Move item"
                    onClick={(e) => e.stopPropagation()}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ")
                        e.stopPropagation();
                    }}
                    {...listeners}
                    {...attributes}
                  >
                    <i className="fa-solid fa-bars" aria-hidden />
                  </div>
                </div>
              ) : null}

              <div className="store-products-row__media-col">
                <div className="store-products-row__image-wrap">
                  {thumbUrl ? (
                    <img
                      className="d-block mw-100 object-fit-contain"
                      style={{ maxHeight: "50px" }}
                      src={thumbUrl}
                      alt=""
                      draggable={false}
                    />
                  ) : (
                    <div className="store-products-row__img-placeholder">
                      No image
                    </div>
                  )}
                </div>
              </div>

              <div className="store-products-row__detail-col">
                <div className="store-products-row__detail-stack">
                  <div
                    className={`store-project-cart-line__name-row d-flex align-items-start justify-content-between gap-2 ${
                      isEditing ? "store-project-cart-line__name-row--editing" : "flex-nowrap"
                    }`}
                  >
                    <div className="store-project-cart-line__name-col flex-grow-1 min-w-0">
                      {isEditing ? (
                        <form
                          onSubmit={handleUpdate}
                          className="store-project-cart-line__name-edit"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {productItemLabel ? (
                            <span
                              className="store-project-cart-line__name-edit-label d-inline-flex align-items-center justify-content-center flex-shrink-0 small fw-semibold text-white border bg-secondary rounded-pill px-1 lh-1"
                              aria-hidden
                            >
                              {productItemLabel}
                            </span>
                          ) : null}
                          <div className="input-group input-group-sm store-project-cart-line__name-edit-group">
                            <input
                              type="text"
                              className="form-control text-dark unmask"
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              autoFocus
                              disabled={isSavingName}
                              aria-label="Product name"
                              onKeyDown={(e) => {
                                e.stopPropagation();
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  discardNameEdit();
                                }
                              }}
                            />
                            <button
                              type="submit"
                              className="btn btn-outline-success"
                              title="Save name"
                              aria-label="Save name"
                              disabled={isSavingName || !editName.trim()}
                            >
                              <i className="fa-solid fa-check" aria-hidden />
                            </button>
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              title="Discard name"
                              aria-label="Discard name"
                              disabled={isSavingName}
                              onClick={(e) => {
                                e.stopPropagation();
                                discardNameEdit();
                              }}
                            >
                              <i className="fa-solid fa-xmark" aria-hidden />
                            </button>
                          </div>
                        </form>
                      ) : (
                        <div
                          className="store-products-row__name d-flex align-items-center gap-1 min-w-0"
                          role={onOpenProductDetail ? "button" : undefined}
                          tabIndex={onOpenProductDetail ? 0 : undefined}
                          onClick={
                            onOpenProductDetail
                              ? handleOpenProductDetailFromLine
                              : undefined
                          }
                          onKeyDown={
                            onOpenProductDetail
                              ? (e) => {
                                  if (e.key !== "Enter" && e.key !== " ")
                                    return;
                                  e.preventDefault();
                                  handleOpenProductDetailFromLine(e);
                                }
                              : undefined
                          }
                        >
                          {productItemLabel ? (
                            <span
                              className="d-inline-flex align-items-center justify-content-center flex-shrink-0 small fw-semibold text-white border bg-secondary rounded-pill px-1 lh-1"
                              style={{
                                minWidth: "1.125rem",
                                height: "1.125rem",
                                fontSize: "0.7rem",
                              }}
                              aria-hidden
                            >
                              {productItemLabel}
                            </span>
                          ) : null}
                          <span className="min-w-0 text-start">
                            {getProjectItemDisplayName(item)}
                          </span>
                          {item.archived === true ? (
                            <span className="badge rounded-pill text-bg-danger flex-shrink-0">
                              Archived
                            </span>
                          ) : null}
                          {showProductRenamePencil ? (
                            <button
                              type="button"
                              className="btn btn-link btn-sm p-0 text-muted flex-shrink-0 shadow-none ms-1"
                              title="Rename item"
                              aria-label="Rename item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditName(getProjectItemDisplayName(item));
                                setIsEditing(true);
                              }}
                            >
                              <i className="fa-solid fa-pencil" aria-hidden />
                            </button>
                          ) : null}
                        </div>
                      )}
                    </div>
                    <div
                      className="store-project-cart-line__price-col flex-shrink-0 text-end"
                      style={{ maxWidth: "42%" }}
                    >
                      <div className="store-project-cart-line__price-col-inner d-flex flex-column align-items-end gap-1">
                        {showPricing && !canShowPrice ? (
                          <div className="store-products-row__price-row justify-content-end mb-0">
                            <button
                              type="button"
                              className="btn btn-link p-0 text-decoration-none small"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                if (typeof onAuthRequired === "function") {
                                  onAuthRequired();
                                  return;
                                }
                                window.location.assign("/register");
                              }}
                            >
                              Login to view price
                            </button>
                          </div>
                        ) : null}
                        {showPricing &&
                        canShowPrice &&
                        (salePriceText || unitText) &&
                        !stalePricePopoverEligible ? (
                          <>
                            {showSampleListPrice ? (
                              <div className="store-products-row__price-row justify-content-end mb-0">
                                <span className="store-products-row__price store-products-row__price--list">
                                  MRP {sampleListPriceText}
                                </span>
                              </div>
                            ) : null}
                            <div className="store-products-row__price-row justify-content-end align-items-baseline mb-0">
                              {salePriceText ? (
                                <span className="store-products-row__price fw-bold text-dark">
                                  {salePriceText}
                                </span>
                              ) : null}
                              {unitText ? (
                                <small className="text-primary fw-normal">
                                  {unitText}
                                </small>
                              ) : null}
                            </div>
                          </>
                        ) : null}
                        {stalePricePopoverEligible && quotedSaleForStaleRow ? (
                          <>
                            {showSampleListPrice ? (
                              <div className="store-products-row__price-row justify-content-end mb-0">
                                <span className="store-products-row__price store-products-row__price--list">
                                  MRP {sampleListPriceText}
                                </span>
                              </div>
                            ) : null}
                            <div className="store-products-row__price-row justify-content-end align-items-baseline mb-0">
                              <button
                                type="button"
                                id={`store-cart-stale-price-${item.id}`}
                                className="store-project-cart-line__stale-price-trigger btn btn-link p-0 border-0 bg-transparent text-danger text-decoration-none d-inline-flex align-items-baseline justify-content-end gap-1 small shadow-none"
                                aria-label="Quoted price on this line. Show catalog updated price."
                                onClick={(e) => {
                                  e.stopPropagation();
                                }}
                              >
                                <CircleAlert
                                  size={16}
                                  className="flex-shrink-0"
                                  aria-hidden
                                />
                                <span className="fw-semibold">
                                  {quotedSaleForStaleRow}
                                </span>
                              </button>
                              <UncontrolledPopover
                                placement="auto"
                                trigger="legacy"
                                target={`store-cart-stale-price-${item.id}`}
                              >
                                <PopoverBody className="small w-auto bg-secondary text-white">
                                  <div className="p-2 fw-bold">
                                    Updated price:{" "}
                                    {formatInr(
                                      Math.round(item.current_discount_price)
                                    )}
                                  </div>
                                </PopoverBody>
                              </UncontrolledPopover>
                            </div>
                            {unitText ? (
                              <div className="store-products-row__price-row justify-content-end mb-0">
                                <small className="text-primary fw-normal">
                                  {unitText}
                                </small>
                              </div>
                            ) : null}
                          </>
                        ) : null}
                      </div>
                    </div>
                  </div>
                  {displaySizeRangeLine ||
                  showAxisVariantUi ||
                  showProductQuickActionsToolbar ? (
                    <div className="d-flex flex-column gap-2 mt-1">
                      {displaySizeRangeLine || showAxisVariantUi ? (
                        <div className="store-project-cart-line__size-row d-flex flex-wrap align-items-center gap-2">
                          {displaySizeRangeLine ? (
                            <span className="store-project-cart-line__size small text-black">
                              {displaySizeRangeLine}
                            </span>
                          ) : null}
                          {showSizeRangeChangeLink &&
                          lineActionsActive &&
                          !isVariantEditorOpen ? (
                            <button
                              type="button"
                              className="store-project-cart-line__size-toggle btn btn-link btn-sm p-0 text-lowercase"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onSelectItem(item.id);
                                setIsVariantEditorOpen(true);
                              }}
                            >
                              change
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                      {showProductQuickActionsToolbar ? (
                        <div className="d-flex">
                          <div
                            className="d-inline-flex align-items-center flex-wrap gap-2"
                            role="group"
                            aria-label="Item actions"
                            data-store-cart-quick-actions
                            onClick={(e) => e.stopPropagation()}
                          >
                            {canCloneItems ? (
                              <button
                                type="button"
                                className="btn btn-sm border-0 bg-transparent shadow-none text-black d-inline-flex align-items-center gap-1 px-1"
                                title="Duplicate this line"
                                aria-label="Duplicate item"
                                onClick={handleCloneItem}
                              >
                                <i className="fa-solid fa-clone" aria-hidden />
                                <span className="small">Duplicate</span>
                              </button>
                            ) : null}
                            <button
                              type="button"
                              className="btn btn-sm border-0 bg-transparent shadow-none text-danger d-inline-flex align-items-center gap-1 px-1"
                              title={
                                canMutateProjectCart
                                  ? "Delete item"
                                  : "Remove item"
                              }
                              aria-label={
                                canMutateProjectCart
                                  ? "Delete item"
                                  : "Remove item"
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                if (canMutateProjectCart) {
                                  setShowDeleteConfirm(true);
                                  return;
                                }
                                if (
                                  canRemoveProductInCartMode &&
                                  typeof onNonCustomerProductDecrement ===
                                    "function"
                                ) {
                                  onNonCustomerProductDecrement(item);
                                }
                              }}
                            >
                              <i className="fa-solid fa-trash" aria-hidden />
                              <span className="small">Remove</span>
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {/* Disabled for testing - do not enable */}
                  {/* {productDeliveryLabel != null ? (
                  <div
                    className="text-success mt-1 d-flex align-items-center gap-1 fw-bold"
                    style={{ fontSize: "0.8rem" }}
                  >
                    <i className="fa-solid fa-truck" aria-hidden />
                    Delivery <span>{productDeliveryLabel}</span>
                    <small className="text-muted fw-normal">(Chennai)</small>
                  </div>
                ) : null} */}
                  {item.notes ? (
                    <p className="store-products-row__desc mb-0">
                      {item.notes}
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
            {showSizeRangeEditorPanel ? (
              <div
                className="store-products-row__controls store-project-cart-line__variant-editor store-project-cart-line__variant-editor--attached w-100 m-0 border"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="px-3 pt-2 pb-2">
                  <>
                    {heightOpts.length > 0 ||
                    widthOpts.length > 0 ||
                    depthOpts.length > 0 ? (
                        <StoreProductVariantDimensionPickers
                          axes={axes}
                          listProductId={listProductId}
                          onPickProductId={handleVariantProductId}
                          disabled={isSavingVariant || !canEditLineLikeProject}
                          keyPrefix="cart"
                          className={`store-products-row__dims-row flex-wrap${
                            rangeOpts.length > 0 ? " mb-2" : ""
                          }`}
                        />
                      ) : null}
                      {rangeOpts.length > 0 ? (
                        <div className="store-products-row__dims-row flex-wrap">
                          <div className="store-products-row__dim-cell">
                            <span className="store-products-row__label">
                              Range
                            </span>
                            <select
                              className="form-select form-select-sm"
                              value={rangePid != null ? String(rangePid) : ""}
                              disabled={
                                isSavingVariant || !canEditLineLikeProject
                              }
                              onChange={(e) =>
                                handleVariantProductId(e.target.value)
                              }
                              aria-label="Range"
                            >
                              <StoreNewVariantAxisSelectOptions
                                axisList={rangeOpts}
                                keyPrefix="r"
                              />
                            </select>
                          </div>
                        </div>
                      ) : null}
                  </>
                  {isSavingVariant ? (
                    <span className="small text-muted d-block mt-1">
                      Saving...
                    </span>
                  ) : null}
                </div>
                <div className="border-top d-flex w-100 px-3 py-2">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary lh-sm small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsVariantEditorOpen(false);
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : null}
          </>
        </div>
      )}

      {showDeleteConfirm && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 2000,
          }}
        >
          <div className="card" style={{ maxWidth: 400 }}>
            <div className="card-body">
              <h6 className="fw-bold mb-3">Confirm Delete</h6>
              <p className="mb-3">
                Are you sure you want to delete "
                {getProjectItemDisplayName(item)}"?
                {isFolder && hasChildren && (
                  <span className="text-danger d-block mt-1">
                    This will also delete all child items.
                  </span>
                )}
              </p>
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
